/**
 * @file preAssignUtils.js
 * @author Simon Tenedero
 * @created 2025-07-28
 * @lastModified 2025-07-28
 * @description module for managing roles
 *
 * NOTE: these functions can be refactored as cloud functions in the future
 */

import {
  doc,
  addDoc,
  deleteDoc,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../firebaseConfig";

/**
 * Pre-assign a role to a user
 *
 * @param {Object} preAssignObject - The object containing pre-assignment details
 * @param {string} preAssignObject.email - The email of the user to pre-assign the role to
 * @param {string} preAssignObject.roleName - The name of the role to pre-assign
 * @param {string} preAssignObject.roleId - The ID of the role to pre-assign
 *
 * @return {Promise<void>} A promise that resolves when the role is pre-assigned
 * @throws {Error} If there is an error pre-assigning the role to the
 */
export const preAssignRole = async (preAssignObject) => {
  const { email, roleName, roleId } = preAssignObject;

  try {
    // Create a new document in the 'preAssignedRoles' collection
    const docRef = await addDoc(collection(db, "preAssignedEmails"), {
      email: email.toLowerCase(), //ensure email is lowercase
      roleName: roleName,
      roleId: roleId,
    });
    console.log("Document written with ID: ", docRef.id);
  } catch (e) {
    console.error("Error adding document: ", e);
  }
};

/**
 * Remove a pre-assigned role for a user based on their email
 *
 * @param {string} email - The email of the user whose pre-assigned role is to be removed
 *
 * @return {Promise<void>} A promise that resolves when the role is removed
 * @throws {Error} If there is an error removing the pre-assigned role from the database
 */
export const removePreAssignedRole = async (email) => {
  console.log("Removing pre-assigned role for email:", email);

  try {
    // Fetch the document by email
    const querySnapshot = await getDocs(collection(db, "preAssignedEmails"));

    querySnapshot.forEach((doc) => {
      if (doc.data().email === email) {
        // Delete the document
        deleteDoc(doc.ref);
        console.log("Document deleted with ID: ", doc.id);
      }
    });
  } catch (e) {
    console.error("Error deleting document: ", e);
  }
};

/**
 * Fetch all pre-assigned roles from the database
 *
 * @returns {Promise<Array>} A promise that resolves to an array of pre-assigned roles
 * @throws {Error} If there is an error fetching pre-assigned roles from the database
 */
export const fetchPreAssignedRoles = async () => {
  try {
    const preAssignedRoles = [];
    const querySnapshot = await getDocs(collection(db, "preAssignedEmails"));

    querySnapshot.forEach((doc) => {
      preAssignedRoles.push({
        ...doc.data(),
        id: doc.id,
      });
    });

    return preAssignedRoles;
  } catch (e) {
    console.error("Error fetching pre-assigned roles: ", e);
    return [];
  }
};
